/**
 * จ่ายนอกรอบเงินเดือน (ค่าคอม/OT) — Owner/HR.
 *
 * ลูกค้าคำนวณค่าคอมเองใน Excel แล้วโอนแยกจากรอบเงินเดือน — หน้านี้คือสมุดบันทึก:
 * สร้างรอบ → ใส่ยอดรายคนแยกประเภท (กรอกมือ / import ไฟล์) → ระบบคิด WHT/สุทธิ →
 * ยืนยัน "จ่ายแล้ว" = ล็อก + พนักงานเห็นสลิปใน LINE (/my-payouts).
 * ไม่เกี่ยวกับรอบเงินเดือนปกติ (payroll run) เลย.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  usePayoutTypes, useUpsertPayoutType,
  usePayoutRounds, useUpsertPayoutRound, usePayoutRound, usePayoutRoundComputed,
  useSavePayoutItems, useMarkPayoutPaid, useCancelPayoutRound, usePayoutReport,
  useReleasePayslipPeriod, useEmployees,
  type PayoutType, type PayoutRoundSummary, type PayoutRoundDetail, type PayoutComputed, type PayoutComputedRow,
} from '../../hooks';
import { apiDownload, apiFetch } from '../../lib/api-client';
import { matchTypeByHeader, newTypeCode } from '../../lib/payout-header';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { Modal } from '../../components/ui/Modal';
import { EmployeeMultiPicker, type PickerEmployee } from '../../components/ui/EmployeePicker';
// กติกา "ใครยังมีสิทธิ์รับเงินของงวดไหน" อยู่ที่ shared ที่เดียว (มีเทสคุม) — ห้ามเขียนซ้ำที่นี่
import { roundPeriodOf, leaverOf, eligibleForPeriod } from '@payroll/shared';
import {
  Plus, ChevronLeft, Save, CheckCircle2, Download, Landmark, Upload,
  Trash2, X, FileSpreadsheet, Lock, Ban, Pencil, AlertTriangle, Info,
} from 'lucide-react';

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** เหตุที่ระบบไม่ให้เบี้ยขยัน — รหัสจาก /hr/payouts/rounds/:id/computed */
const DILIGENCE_REASON_LABEL: Record<string, string> = {
  wfh_or_exempt: 'ทำงานที่บ้าน/ยกเว้นสแกน',
  no_summary: 'ยังไม่ได้สรุปเวลาใหม่',
  no_attendance: 'ไม่มีวันมาทำงาน',
  absent: 'มีวันขาด',
  not_before_shift: 'เข้างานไม่ก่อนเวลาเริ่มกะ',
  leave: 'มีการลาที่ตัดสิทธิ์',
  leave_pending: 'มีใบลารอตัดสิน',
  rules_unavailable: 'ยังไม่ได้รัน SQL',
  not_commission: 'กฎเดิม: เฉพาะคนมีค่าคอม',
  late: 'กฎเดิม: มีวันสาย',
};
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const thaiDate = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return `${d} ${THAI_MONTHS_SHORT[(m ?? 1) - 1]} ${(y ?? 0) + 543}`;
};

const STATUS_BADGE: Record<PayoutRoundSummary['status'], { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'badge-warn' },
  paid: { label: 'จ่ายแล้ว', cls: 'badge-success' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

interface EmpRow {
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname?: string | null;
  department?: string | null;
  position?: string | null;
  status?: string | null;
  end_date?: string | null;
}

/** แถวในตารางแก้ไข — เก็บเป็น string ระหว่างพิมพ์ กัน NaN */
interface EditRow {
  emp_code: string;
  name: string;
  dept: string;
  values: Record<string, string>;  // type_code → amount (string)
  wht: string;                     // percent
  whtAmt?: string;                 // override เป็นยอดบาท (จากไฟล์ลูกค้า) — '0' = ตั้งใจหัก 0 ต่างจาก undefined = ไม่มี override
}

export function HRPayouts() {
  const [view, setView] = useState<'rounds' | 'report' | 'types'>('rounds');
  const [roundId, setRoundId] = useState<string | null>(null);

  return (
    <AdminLayout title="จ่ายนอกรอบ (ค่าคอม/OT)">
      {roundId ? (
        <RoundDetail id={roundId} onBack={() => setRoundId(null)} />
      ) : (
        <>
          <div className="flex gap-1 mb-3 border-b border-gray-200">
            {([['rounds', 'รอบจ่าย'], ['report', 'สรุปทั้งปี'], ['types', 'ประเภทรายการ']] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`px-3 py-2 text-sm font-medium border-b-2 ${view === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {view === 'rounds' ? <RoundList onOpen={setRoundId} /> : view === 'report' ? <YearReport /> : <TypesTab />}
        </>
      )}
    </AdminLayout>
  );
}

// === รายการรอบจ่าย ===
function RoundList({ onOpen }: { onOpen: (id: string) => void }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const rounds = usePayoutRounds(year);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        บันทึกการจ่ายค่าคอม/OT ที่โอนแยกจากรอบเงินเดือน — ใส่ยอดที่คำนวณไว้แล้ว ระบบเก็บประวัติ คิดหัก ณ ที่จ่าย และออกสลิปให้พนักงาน
      </p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-auto">
          {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>ปี {y + 543}</option>
          ))}
        </select>
        <button onClick={() => setCreating(true)} className="btn-cta inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> สร้างรอบจ่าย
        </button>
      </div>

      {rounds.isLoading ? (
        <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>
      ) : (rounds.data ?? []).length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีรอบจ่ายในปีนี้ — กด "สร้างรอบจ่าย" เพื่อเริ่ม</p>
      ) : (
        <div className="space-y-2">
          {(rounds.data ?? []).map((r) => (
            <button key={r.id} onClick={() => onOpen(r.id)} className="card p-3 w-full text-left hover:bg-gray-50 transition">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                    {r.status === 'paid' && <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                    {r.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {/* งวดต้องเห็นตั้งแต่หน้ารายการ — รอบที่งวดเดียวกันจะรวมเป็นสลิปใบเดียว */}
                    {r.period && <>งวด {periodLabel(r.period)} · </>}
                    จ่ายวันที่ {thaiDate(r.pay_date)} · {r.totals.people} คน
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={STATUS_BADGE[r.status].cls}>{STATUS_BADGE[r.status].label}</span>
                  <div className="text-sm font-semibold text-gray-900 mt-1">฿{fmt(r.totals.net)}</div>
                  {((r.totals.deduct ?? 0) > 0 || r.totals.wht > 0) && (
                    <div className="text-[11px] text-gray-400">
                      หัก ฿{fmt((r.totals.deduct ?? 0) + r.totals.wht)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <RoundMetaModal onClose={() => setCreating(false)} onCreated={onOpen} />}
    </div>
  );
}

// === งวดของรอบจ่าย ===
// ⭐ "งวด" = เดือนที่คิดเงิน ไม่ใช่เดือนที่โอน — ค่าคอมจ่ายวันที่ 15 ของเดือนถัดไป
// งวดจึงเป็นเดือนก่อนวันโอน. เดิมหน้าจอไม่มีช่องนี้ หลังบ้านเลยต้องเดาจากวันโอน
// → ค่าคอมของ มิ.ย. ที่โอน 15 ก.ค. ไปโผล่บนสลิปงวด ก.ค. (เคสจริง 2026-07-29)
const TH_MON_ABBR_FE: Record<string, number> = {
  'ม.ค.': 1, 'ก.พ.': 2, 'มี.ค.': 3, 'เม.ย.': 4, 'พ.ค.': 5, 'มิ.ย.': 6,
  'ก.ค.': 7, 'ส.ค.': 8, 'ก.ย.': 9, 'ต.ค.': 10, 'พ.ย.': 11, 'ธ.ค.': 12,
};
const TH_MON_FULL_FE = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

function shiftMonth(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number);
  const idx = (y ?? 0) * 12 + ((m ?? 1) - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}
function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return TH_MON_FULL_FE[m ?? 0] ? `${TH_MON_FULL_FE[m!]} ${(y ?? 0) + 543}` : period;
}
/** ชื่อรอบบอกเดือนแต่ไม่บอกปี → เอาปีที่ทำให้เดือนนั้นใกล้วันโอนที่สุด (ธ.ค. ที่โอน ม.ค. = ปีก่อน) */
function nearestYearFor(mon: number, payDate: string): number {
  const [py, pm] = payDate.slice(0, 7).split('-').map(Number);
  const target = (py ?? 0) * 12 + ((pm ?? 1) - 1);
  return [-1, 0, 1]
    .map((d) => (py ?? 0) + d)
    .reduce((best, y) => (Math.abs(y * 12 + (mon - 1) - target) < Math.abs(best * 12 + (mon - 1) - target) ? y : best));
}
/**
 * ⭐ ชื่อเดือนที่คนพิมพ์เองสะกดเพี้ยนได้ — ของจริงเจอ "กรกฏาคม" (ฏ ปฏัก) แทน
 * "กรกฎาคม" (ฎ ชฎา) สองตัวนี้หน้าตาเกือบเหมือนกัน ถ้าจับคำเต็มจะพลาดเงียบ ๆ
 * แล้วตกไปใช้กติกาวันโอน (เงินเดือนที่จ่ายข้ามเดือนจะได้งวดผิดทันที).
 * จึงจับด้วย "ส่วนต้นของคำที่ไม่มีตัวกำกวม" — ตรวจแล้วไม่ชนกันเองสักคู่
 */
const TH_MON_PREFIX: Array<[string, number]> = [
  ['มกรา', 1], ['กุมภา', 2], ['มีนา', 3], ['เมษา', 4], ['พฤษภา', 5], ['มิถุนา', 6],
  ['กรก', 7], ['สิงหา', 8], ['กันยา', 9], ['ตุลา', 10], ['พฤศจิ', 11], ['ธันวา', 12],
];
/** ปีจากข้อความหลังชื่อเดือน — รับเฉพาะเลขที่เป็นปีได้จริง (กัน "รอบจ่าย 15" กลายเป็นปี) */
function yearFromText(s: string): number | null {
  for (const g of s.match(/\d{2,4}/g) ?? []) {
    const n = Number(g);
    if (n >= 2400 && n <= 2600) return n - 543;   // พ.ศ. เต็ม 2569 → 2026
    if (n >= 1900 && n <= 2100) return n;         // ค.ศ. เต็ม
    if (n >= 50 && n <= 99) return 2500 + n - 543; // พ.ศ. ย่อ 69 → 2569 → 2026
  }
  return null;
}
/**
 * เดางวดจากชื่อรอบก่อน แล้วค่อยใช้กติการอบจ่าย.
 * ⭐ ชื่อรอบชนะเสมอ เพราะลูกค้าตั้งชื่อรอบตาม "เดือนที่คิดเงิน" ไม่ใช่เดือนที่โอน
 * (เช่น "เงินเดือนมิถุนายน2569" โอนจริง 20 ก.ค. → งวด มิ.ย.)
 */
function guessPeriod(title: string, payDate: string): string {
  let mon = 0; let at = -1;
  for (const [prefix, n] of TH_MON_PREFIX) {
    const i = title.indexOf(prefix);
    if (i >= 0 && (at < 0 || i < at)) { at = i; mon = n; }
  }
  if (!mon) {  // เดือนย่อมีจุดคั่น สะกดเพี้ยนยาก จับคำตรง ๆ ได้
    const abbr = title.match(/ม\.ค\.|ก\.พ\.|มี\.ค\.|เม\.ย\.|พ\.ค\.|มิ\.ย\.|ก\.ค\.|ส\.ค\.|ก\.ย\.|ต\.ค\.|พ\.ย\.|ธ\.ค\./);
    if (abbr) { mon = TH_MON_ABBR_FE[abbr[0]]!; at = abbr.index!; }
  }
  if (mon > 0) {
    return `${yearFromText(title.slice(at)) ?? nearestYearFor(mon, payDate)}-${String(mon).padStart(2, '0')}`;
  }
  // ชื่อรอบไม่บอกเดือน: ค่าคอม/OT จ่ายเดือนถัดไป = งวดเดือนก่อนวันโอน · เงินเดือน = เดือนที่โอน
  const byMonth = payDate.slice(0, 7);
  return /คอม|ot|โอที/i.test(title) ? shiftMonth(byMonth, -1) : byMonth;
}

// === modal สร้าง/แก้ข้อมูลรอบ ===
function RoundMetaModal({ initial, onClose, onCreated }: {
  initial?: { id: string; title: string; pay_date: string; wht_default_percent: number; note: string | null; period: string | null };
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const upsert = useUpsertPayoutRound();
  const toast = useToast();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [payDate, setPayDate] = useState(initial?.pay_date ?? new Date().toISOString().slice(0, 10));
  const [wht, setWht] = useState(String(initial?.wht_default_percent ?? 3));
  const [note, setNote] = useState(initial?.note ?? '');
  // งวดที่ HR แก้เอง ห้ามให้ค่าเดาทับ — จำไว้ว่าแตะแล้วหรือยัง
  const [periodTouched, setPeriodTouched] = useState(Boolean(initial?.period));
  const [period, setPeriod] = useState(initial?.period ?? guessPeriod(initial?.title ?? '', initial?.pay_date ?? new Date().toISOString().slice(0, 10)));
  const guessed = guessPeriod(title, payDate);
  const effectivePeriod = periodTouched ? period : guessed;

  // ⭐ M3 audit fix: เดิม upsert.mutate() ไม่ await + ปุ่มพึ่ง upsert.isPending อย่าง
  // เดียวกันเบิ้ล — กดเบิ้ลเร็ว (double-click / double-tap มือถือ) ก่อน React
  // re-render disabled ทัน = ยิง 2 คำขอสร้างรอบพร้อมกัน = 2 รอบชื่อ/งวดเดียวกัน
  // (เคยเจอจริง). submittingRef กันซ้อนแบบ synchronous ไม่รอ re-render + await
  // mutateAsync ให้ error (รวม 409 "มีรอบชื่อนี้ในงวดนี้แล้ว" จาก DB unique) โผล่
  // เป็น toast ปกติ
  const submittingRef = useRef(false);
  async function save() {
    if (submittingRef.current) return;
    if (!title.trim()) { toast.error('ตั้งชื่อรอบก่อน เช่น "ค่าคอม+OT พ.ค. 69"'); return; }
    if (!/^\d{4}-\d{2}$/.test(effectivePeriod)) { toast.error('เลือกงวด (เดือนที่คิดเงิน) ก่อน'); return; }
    submittingRef.current = true;
    try {
      const r = await upsert.mutateAsync(
        { id: initial?.id, title: title.trim(), pay_date: payDate, wht_default_percent: Number(wht) || 0, note: note || undefined, period: effectivePeriod },
      );
      toast.success('บันทึกรอบแล้ว');
      onClose();
      if (!initial) onCreated?.(r.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      submittingRef.current = false;
    }
  }

  return (
    <Modal open onClose={onClose} title={initial ? 'แก้ข้อมูลรอบ' : 'สร้างรอบจ่าย'}>
      <div className="space-y-3">
        <div>
          <label className="label">ชื่อรอบ *</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='เช่น "ค่าคอม+OT พ.ค. 69"' />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">วันที่จ่าย/โอน *</label>
            <input type="date" className="input" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <div>
            <label className="label">งวด (เดือนที่คิดเงิน) *</label>
            <input
              type="month"
              className="input"
              value={effectivePeriod}
              onChange={(e) => { setPeriodTouched(true); setPeriod(e.target.value); }}
            />
            <p className="text-[11px] text-gray-500 mt-0.5">
              สลิปจะไปอยู่ในงวด <b>{periodLabel(effectivePeriod)}</b>
              {!periodTouched && <> (ระบบเลือกให้ แก้ได้)</>}
            </p>
          </div>
        </div>
        <div className="rounded-md bg-gray-50 border border-gray-200 px-2.5 py-2 text-[11px] text-gray-600 leading-relaxed">
          <b>งวด ≠ เดือนที่โอนเงิน</b> · เงินเดือนจ่ายสิ้นเดือน = งวดเดือนนั้น ·
          ค่าคอม/OT จ่ายวันที่ 15 เดือนถัดไป = <b>งวดเดือนก่อนหน้า</b>
          <br />ทุกรอบที่งวดเดียวกันจะรวมเป็นสลิปใบเดียว — ตั้งงวดผิด ยอดจะไปโผล่ผิดเดือน
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">หัก ณ ที่จ่าย (%) ค่าตั้งต้น</label>
            <input type="number" step="0.01" min="0" className="input" value={wht} onChange={(e) => setWht(e.target.value)} />
            <p className="text-[11px] text-gray-400 mt-0.5">
              ปรับรายคนได้ในตาราง (ใส่ 0 = ไม่หัก) · ใช้กับเงินได้ในรอบนี้เท่านั้น
              ไม่เกี่ยวกับภาษีเงินเดือน
            </p>
          </div>
        </div>
        <div>
          <label className="label">หมายเหตุ</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button onClick={() => void save()} disabled={upsert.isPending} className="btn-cta w-full justify-center flex items-center gap-2">
          <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}

// === รายละเอียดรอบ + ตารางแก้ไข ===
function RoundDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const detail = usePayoutRound(id);
  const types = usePayoutTypes();
  // includeLeft — ต้องเห็นคนที่ลาออกด้วย ไม่งั้นค่าคอมงวดสุดท้ายของเขาตกหล่นเงียบ
  // (กรองด้วย eligibleForPeriod ทีหลัง ไม่ได้เอามาโชว์หมดทุกงวด)
  const { data: employees } = useEmployees(true, true) as { data: EmpRow[] | undefined };
  const saveItems = useSavePayoutItems();
  const markPaid = useMarkPayoutPaid();
  const cancelRound = useCancelPayoutRound();
  const releaseSlip = useReleasePayslipPeriod();
  const toast = useToast();
  const confirm = useConfirm();

  const [rows, setRows] = useState<EditRow[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);

  const round = detail.data?.round;
  const isDraft = round?.status === 'draft';
  const isPaid = round?.status === 'paid';
  const isCancelled = round?.status === 'cancelled';
  const [slipLoading, setSlipLoading] = useState<string | null>(null);
  // ⭐ M3: ไฟล์โอนธนาคาร = คำสั่งโอนเงินจริง — เตือนก่อนโหลดซ้ำ (ไม่ห้ามตาย เผื่อไฟล์
  // เดิมหาย) ด้วยเวลา export ล่าสุดที่ backend บันทึกไว้ (ดู bank_transfer_exported_at)
  const [bankExporting, setBankExporting] = useState(false);
  async function handleExportBankTransfer() {
    if (bankExporting || !round) return;
    if (round.bank_transfer_exported_at) {
      const ok = await confirm({
        title: 'เคย export ไฟล์นี้ไปแล้ว',
        message: `เคย export ไฟล์โอนธนาคารของรอบนี้แล้วเมื่อ ${new Date(round.bank_transfer_exported_at).toLocaleString('th-TH')} — โหลดซ้ำอาจทำให้โอนเงินซ้ำถ้ายังไม่ได้ตรวจสอบ ต้องการโหลดอีกครั้งหรือไม่?`,
        confirmText: 'โหลดซ้ำ', danger: true,
      });
      if (!ok) return;
    }
    setBankExporting(true);
    try {
      await apiDownload(`/hr/payouts/rounds/${id}/bank-transfer`, `payout-bank-${round.pay_date}.csv`);
      await detail.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ออกไฟล์โอนไม่สำเร็จ');
    } finally {
      setBankExporting(false);
    }
  }
  async function downloadSlip(empCode: string) {
    if (slipLoading) return;
    setSlipLoading(empCode);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(`/hr/payouts/rounds/${id}/slip-pdf?emp=${encodeURIComponent(empCode)}`);
      window.open(pdf_url, '_blank');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างสลิปไม่สำเร็จ');
    } finally {
      setSlipLoading(null);
    }
  }
  const activeTypes = useMemo(() => (types.data ?? []).filter((t) => t.active), [types.data]);
  const deductCodes = useMemo(() => new Set(activeTypes.filter((t) => t.kind === 'deduction').map((t) => t.code)), [activeTypes]);
  const hasDeduct = deductCodes.size > 0;
  const empByCode = useMemo(() => new Map((employees ?? []).map((e) => [e.emp_code, e])), [employees]);
  // งวดของรอบนี้ = ตัวตัดสินว่าคนที่ลาออกแล้วยังมีสิทธิ์รับเงินรอบนี้ไหม
  const roundPeriod = useMemo(() => roundPeriodOf(round), [round]);
  const eligibleEmployees = useMemo(
    () => (employees ?? []).filter((e) => eligibleForPeriod(e, roundPeriod)),
    [employees, roundPeriod],
  );
  /** คนที่อยู่ในไฟล์นำเข้าแต่ลาออกไปก่อนงวดนี้ — ต้องให้ยืนยันทีละคน ไม่ข้ามเงียบ */
  const [pendingLeavers, setPendingLeavers] = useState<Array<{ emp_code: string; name: string; endDate: string | null; row: EditRow }>>([]);

  // ⚠️ กับดักหักภาษีซ้ำ 2 ชั้น (Mangmee 2026-07-22): บาง tenant seed payout_type
  // ประเภท "หัก" ชื่อ "ภาษีหัก ณ ที่จ่าย" มาจากไฟล์ Excel ของลูกค้า. ถ้ากรอกยอดใน
  // คอลัมน์นั้นด้วย จะโดนหักทั้งคอลัมน์นั้น + ช่อง "หัก ณ ที่จ่าย" ของรอบ = 2 ชั้น
  // (เคยทำค่าคอมหักเกินจริงมาแล้ว). ตรวจแล้วเตือนบนหน้ารอบ ไม่ให้เงียบ
  // + ชีทเงินเดือน (2026-11-01) มีคอลัมน์ "ภาษี 3%" / "ภ.ง.ด.1" ที่ HR คิดยอดเอง — ซ้ำกับ % ของรอบเหมือนกัน
  const whtLikeTypes = useMemo(
    () => activeTypes.filter((t) =>
      t.kind === 'deduction' &&
      (['wht', 'tax_3pct', 'pnd1'].includes(t.code) || /ณ\s*ที่จ่าย|ภาษี|ภ\.?\s*ง\.?\s*ด|withhold|\bwht\b/i.test(t.label))),
    [activeTypes],
  );

  // โหลดรายการจาก server → state แก้ไข (รีเซ็ตเมื่อเปลี่ยนรอบ/refetch หลัง save)
  useEffect(() => {
    if (!detail.data) return;
    setRows(detail.data.items.map((it) => ({
      emp_code: it.emp_code,
      name: it.employee ? `${it.employee.first_name} ${it.employee.last_name}`.trim() + (it.employee.nickname ? ` (${it.employee.nickname})` : '') : it.emp_code,
      dept: it.employee?.department ?? '',
      values: Object.fromEntries(Object.entries(it.breakdown).map(([k, v]) => [k, String(v)])),
      wht: String(it.wht_percent),
    })));
    setRemoved(new Set());
    setDirty(false);
  }, [detail.data]);

  // สุทธิ = เงินเพิ่ม - เงินหัก - WHT (WHT คิดจากยอดหลังหัก ติดลบไม่คิด — ตรงกับ worker)
  const calc = (r: EditRow) => {
    let earn = 0; let deduct = 0;
    for (const [k, v] of Object.entries(r.values)) {
      const n = Number(v) || 0;
      if (deductCodes.has(k)) deduct += n; else earn += n;
    }
    const base = earn - deduct;
    // ถ้าแถวนี้มี override เป็นยอดบาท (จากไฟล์ import) ใช้ยอดนั้นตรงๆ — clamp เข้า [0, base] กัน net ติดลบ
    // เช็คด้วย finite ไม่ใช่ truthy: whtAmt='0' ต้องแปลว่าหัก 0 จริง ไม่ใช่ "ไม่มี override"
    if (r.whtAmt !== undefined && Number.isFinite(Number(r.whtAmt))) {
      const whtAmt = base > 0 ? Math.min(Math.max(Number(r.whtAmt), 0), base) : 0;
      return { earn, deduct, whtAmt, net: base - whtAmt };
    }
    // อัตรา WHT ต้องอยู่ 0–100% เสมอ (กันค่าเพี้ยน เช่น 197.2 ที่มาจากยอดบาท → เงินติดลบ)
    const whtPct = Math.min(100, Math.max(0, Number(r.wht) || 0));
    const whtAmt = base > 0 ? base * whtPct / 100 : 0;
    return { earn, deduct, whtAmt, net: base - whtAmt };
  };
  const totals = rows.reduce(
    (t, r) => { const c = calc(r); return { earn: t.earn + c.earn, deduct: t.deduct + c.deduct, wht: t.wht + c.whtAmt, net: t.net + c.net }; },
    { earn: 0, deduct: 0, wht: 0, net: 0 },
  );
  // คนที่โดนภาษี 2 ชั้นจริง = มียอดในคอลัมน์ภาษี และยังโดน "หัก ณ ที่จ่าย" ของรอบด้วย
  const doubleTaxRows = isDraft
    ? rows.filter((r) => whtLikeTypes.some((t) => Number(r.values[t.code])) && calc(r).whtAmt > 0)
    : [];
  function zeroWhtFor(codes: string[]) {
    const set = new Set(codes);
    setRows((rs) => rs.map((r) => {
      if (!set.has(r.emp_code)) return r;
      const { whtAmt: _whtAmt, ...rest } = r;
      return { ...rest, wht: '0' };
    }));
    setDirty(true);
  }

  function setValue(emp: string, type: string, v: string) {
    setRows((rs) => rs.map((r) => (r.emp_code === emp ? { ...r, values: { ...r.values, [type]: v } } : r)));
    setDirty(true);
  }
  function setWhtRow(emp: string, v: string) {
    setRows((rs) => rs.map((r) => (r.emp_code === emp ? { ...r, wht: v } : r)));
    setDirty(true);
  }
  function clearWhtOverride(emp: string) {
    setRows((rs) => rs.map((r) => {
      if (r.emp_code !== emp) return r;
      const { whtAmt: _whtAmt, ...rest } = r;
      return rest;
    }));
    setDirty(true);
  }
  function removeRow(emp: string) {
    setRows((rs) => rs.filter((r) => r.emp_code !== emp));
    setRemoved((s) => new Set(s).add(emp));
    setDirty(true);
  }
  function addEmployees(codes: string[]) {
    const defaultWht = String(round?.wht_default_percent ?? 3);
    setRows((rs) => {
      const have = new Set(rs.map((r) => r.emp_code));
      const added = codes.filter((c) => !have.has(c)).map((c) => {
        const e = empByCode.get(c);
        return {
          emp_code: c,
          name: e ? `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '') : c,
          dept: e?.department ?? '',
          values: {},
          wht: defaultWht,
        };
      });
      return [...rs, ...added];
    });
    setRemoved((s) => { const next = new Set(s); codes.forEach((c) => next.delete(c)); return next; });
    setDirty(true);
  }

  async function handleSave(): Promise<boolean> {
    try {
      await saveItems.mutateAsync({
        round_id: id,
        items: rows.map((r) => ({
          emp_code: r.emp_code,
          breakdown: Object.fromEntries(
            Object.entries(r.values).map(([k, v]) => [k, Number(v) || 0]).filter(([, v]) => v !== 0),
          ) as Record<string, number>,
          wht_percent: Math.min(99, Math.max(0, Number(r.wht) || 0)),
          ...(r.whtAmt !== undefined && Number.isFinite(Number(r.whtAmt))
            ? { wht_amount: Math.max(0, Number(r.whtAmt)) }
            : {}),
        })),
        remove_emp_codes: [...removed],
      });
      toast.success('บันทึกแล้ว');
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
      return false;
    }
  }

  async function handleMarkPaid() {
    if (dirty && !(await handleSave())) return;
    const ok = await confirm({
      title: 'ยืนยันว่าจ่ายรอบนี้แล้ว?',
      message: `${rows.length} คน · ยอดสุทธิ ฿${fmt(totals.net)} (${totals.deduct > 0 ? `รายการหัก ฿${fmt(totals.deduct)} · ` : ''}หัก ณ ที่จ่าย ฿${fmt(totals.wht)}) — ยืนยันแล้วรอบจะถูกล็อก แก้ไขไม่ได้ และพนักงานจะเห็นสลิปใน LINE`,
      confirmText: 'ยืนยันจ่ายแล้ว',
    });
    if (!ok) return;
    markPaid.mutate(id, {
      onSuccess: () => toast.success('บันทึกว่าจ่ายแล้ว — รอบถูกล็อก'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: 'ยกเลิกรอบนี้?',
      message: round?.status === 'paid'
        ? 'รอบนี้บันทึกว่าจ่ายแล้ว — การยกเลิกจะลบออกจากสลิปพนักงานและรายงาน (ข้อมูลยังเก็บไว้ตรวจย้อนได้)'
        : 'ร่างรอบนี้จะถูกยกเลิก',
      confirmText: 'ยกเลิกรอบ', danger: true,
    });
    if (!ok) return;
    cancelRound.mutate(id, {
      onSuccess: () => { toast.success('ยกเลิกรอบแล้ว'); onBack(); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  async function handleReleaseSlip() {
    if (!round?.period) { toast.error('รอบนี้ยังไม่มีงวด (period)'); return; }
    const ok = await confirm({
      title: 'อนุมัติออกสลิปงวดนี้?',
      message: 'พนักงานจะโหลดสลิปเงินเดือนงวดนี้ได้ทันที (โดยไม่ต้องรอจ่ายค่าคอม) — ใช้กับงวดที่ไม่มีค่าคอม',
      confirmText: 'อนุมัติออกสลิป',
    });
    if (!ok) return;
    releaseSlip.mutate(round.period, {
      onSuccess: () => toast.success('อนุมัติออกสลิปงวดนี้แล้ว — พนักงานโหลดสลิปได้'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  // === import ไฟล์ (xlsx/csv): คอลัมน์แรก = รหัสพนักงาน, หัวคอลัมน์ที่เหลือจับคู่ชื่อ/โค้ดประเภท ===
  async function handleImport(file: File) {
    try {
      const XLSX = await import('xlsx');
      const wb = file.name.endsWith('.csv')
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
      if (aoa.length < 2) { toast.error('ไฟล์ว่าง — ต้องมีหัวตาราง + ข้อมูลอย่างน้อย 1 แถว'); return; }

      const header = aoa[0]!.map((h) => String(h).trim());
      const typeByHeader = new Map<number, string>();
      let codeCol = -1; let whtCol = -1;
      // นับคนที่คอลัมน์ WHT ในไฟล์เว้นว่าง/≤0 = ระบบตีความว่า "ไม่หัก" — ต้องบอก
      // ให้รู้หลัง import ไม่งั้นเงียบจนไปเห็นตอนสลิปออก (เคสจริง 2026-07-22)
      let whtZero = 0;
      header.forEach((h, idx) => {
        const lower = h.toLowerCase();
        if (codeCol < 0 && (lower === 'emp_code' || h === 'รหัส' || h === 'รหัสพนักงาน')) { codeCol = idx; return; }
        // คอลัมน์ "หัก ณ ที่จ่าย" ในไฟล์ลูกค้าเป็น "ยอดบาท" (ไม่ใช่ %) — จับแยกไว้ก่อนจับคู่ประเภทรายการเสมอ
        // กันไม่ให้หลุดไปแมตช์กับประเภท "ภาษีหัก ณ ที่จ่าย" แล้วถูกหักซ้ำกับ WHT default ของรอบ
        if (whtCol < 0 && (lower.includes('wht') || h.includes('หัก ณ ที่จ่าย') || h.includes('ภาษีหัก'))) { whtCol = idx; return; }
        const code = matchTypeByHeader(h, activeTypes);
        if (code) typeByHeader.set(idx, code);
      });
      if (codeCol < 0) { toast.error('ไม่พบคอลัมน์ "รหัสพนักงาน" (emp_code) — ใช้เทมเพลตที่ดาวน์โหลดจากหน้านี้'); return; }
      if (typeByHeader.size === 0) { toast.error('ไม่พบคอลัมน์ประเภทรายการที่ตรงกับระบบ — หัวคอลัมน์ต้องตรงกับชื่อประเภท'); return; }
      // คอลัมน์ตัวเลขที่ไม่ตรงประเภทไหน = ยอดนั้นไม่เข้าสลิป → ต้องบอก (เดิมทิ้งเงียบ เช่น "หักชีท.jst.")
      // ยกเว้นยอดรวม/คงเหลือ ที่ระบบคิดเองอยู่แล้ว
      const skippedCols = header.filter((h, idx) =>
        h && idx !== codeCol && idx !== whtCol && !typeByHeader.has(idx) &&
        !/คงเหลือ|สุทธิ|รวม|net|total/i.test(h) &&
        aoa.slice(1).some((raw) => { const n = Number(String(raw[idx] ?? '').replace(/,/g, '')); return String(raw[idx] ?? '').trim() !== '' && Number.isFinite(n) && n !== 0; }));
      // ไฟล์คิดภาษีมาเองในคอลัมน์ประเภท (ภาษี 3% / ภ.ง.ด.1) และไม่มีคอลัมน์ "หัก ณ ที่จ่าย"
      // → ไม่ใช้ % ของรอบซ้ำอีกชั้น (เว้นว่าง = คนนั้นไม่เสียภาษี เหมือนกติกาคอลัมน์ WHT)
      const taxCodes = new Set(whtLikeTypes.map((t) => t.code));
      const fileHasTaxCols = whtCol < 0 && [...typeByHeader.values()].some((c) => taxCodes.has(c));

      const defaultWht = String(round?.wht_default_percent ?? 3);
      let matched = 0; const unknown: string[] = [];
      // ⭐ แยก "รหัสไม่มีในระบบจริง" (พิมพ์ผิด) ออกจาก "คนนี้ลาออกไปก่อนงวดนี้" —
      // เดิมรวมเป็น "ข้ามรหัสที่ไม่พบ" ก้อนเดียว อ่านเหมือนพิมพ์ผิด เงินเลยตกหล่นเงียบ
      const needConfirm: Array<{ emp_code: string; name: string; endDate: string | null; row: EditRow }> = [];
      const imported = new Map<string, EditRow>();
      for (const raw of aoa.slice(1)) {
        const code = String(raw[codeCol] ?? '').trim();
        if (!code) continue;
        if (!empByCode.has(code)) { unknown.push(code); continue; }
        const e = empByCode.get(code)!;
        const values: Record<string, string> = {};
        for (const [idx, typeCode] of typeByHeader) {
          const n = Number(String(raw[idx] ?? '').replace(/,/g, ''));
          if (Number.isFinite(n) && n !== 0) values[typeCode] = String(n);
        }
        if (Object.keys(values).length === 0) continue;
        // มีคอลัมน์ WHT ในไฟล์ → ใช้เป็น override ยอดบาทเสมอ (ช่องว่าง/ไม่ใช่ตัวเลข/≤0 = หัก 0 จริง เช่น พนักงานประกันสังคม)
        let whtAmt: string | undefined;
        if (whtCol >= 0) {
          const n = Number(String(raw[whtCol] ?? '').replace(/,/g, ''));
          whtAmt = Number.isFinite(n) && n > 0 ? String(n) : '0';
          if (whtAmt === '0') whtZero++;
        } else if (fileHasTaxCols) {
          whtAmt = '0';
        }
        const row: EditRow = {
          emp_code: code,
          name: `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : ''),
          dept: e.department ?? '',
          values,
          wht: defaultWht,
          whtAmt,
        };
        // ลาออกไปก่อนงวดนี้ (หรือยังไม่กรอกวันสิ้นสุด) → พักไว้ให้ยืนยันทีละคน
        if (!eligibleForPeriod(e, roundPeriod)) {
          needConfirm.push({ emp_code: code, name: row.name, endDate: leaverOf(e).endDate, row });
          continue;
        }
        imported.set(code, row);
        matched++;
      }
      if (needConfirm.length > 0) setPendingLeavers(needConfirm);
      if (matched === 0 && needConfirm.length === 0) { toast.error('ไม่มีแถวที่นำเข้าได้ — เช็ครหัสพนักงานและยอดในไฟล์'); return; }
      // ทับแถวเดิมที่รหัสซ้ำ + เพิ่มแถวใหม่
      setRows((rs) => [...rs.filter((r) => !imported.has(r.emp_code)), ...imported.values()]
        .sort((a, b) => a.emp_code.localeCompare(b.emp_code)));
      setRemoved((s) => { const next = new Set(s); imported.forEach((_, c) => next.delete(c)); return next; });
      setDirty(true);
      // บอกให้ชัดว่าระบบตีความ "หัก ณ ที่จ่าย" จากไฟล์ยังไง — เดิมเงียบสนิท
      // คนเลยไม่รู้ว่าไฟล์ทับค่า % ของรอบไปแล้ว
      const whtNote = whtCol >= 0
        ? ` · หัก ณ ที่จ่าย ใช้ยอดบาทจากไฟล์${whtZero > 0 ? ` (${whtZero} คนเว้นว่าง = ไม่หัก)` : ''}`
        : fileHasTaxCols
          ? ' · ภาษีใช้ยอดจากคอลัมน์ในไฟล์ (ไม่หัก % ของรอบซ้ำ)'
          : ` · หัก ณ ที่จ่าย ใช้ ${defaultWht}% ของรอบ (ไฟล์ไม่มีคอลัมน์นี้)`;
      const leaverNote = needConfirm.length ? ` · มี ${needConfirm.length} คนที่ลาออกไปแล้ว รอให้ยืนยัน` : '';
      toast.success(`นำเข้า ${matched} คน${unknown.length ? ` · ข้ามรหัสที่ไม่มีในระบบ ${unknown.length} รายการ (${unknown.slice(0, 5).join(', ')}${unknown.length > 5 ? '…' : ''})` : ''}${leaverNote}${whtNote}`);
      if (skippedCols.length > 0) {
        toast.error(`คอลัมน์ที่ไม่ได้นำเข้า (ไม่ตรงประเภทรายการ จึงไม่ขึ้นสลิป): ${skippedCols.join(', ')} — เพิ่มประเภทชื่อนี้ที่แท็บ "ประเภทรายการ" แล้วนำเข้าใหม่`);
      }
    } catch (e) {
      toast.error(`อ่านไฟล์ไม่สำเร็จ: ${e instanceof Error ? e.message : 'รูปแบบไฟล์ไม่รองรับ'}`);
    }
  }

  // เทมเพลต CSV: ทุกคนที่มีสิทธิ์ในงวดนี้ (รวมคนที่ลาออกในงวดนี้ — เดิมตกหล่น) + คอลัมน์ประเภท
  // ระบบเติมค่าที่คิดให้ต่อคน: วันหยุดนักขัต (ทุกคน) / เบี้ยขยัน (กฎใหม่ทุกคน · กฎเดิมเฉพาะคนมีค่าคอม)
  // / สาย + ลืมสแกน (เฉพาะคนมีค่าคอม) — ลืมสแกนจับจาก code ที่ระบบสร้างให้ (เปลี่ยนชื่อประเภทได้ไม่พัง)
  // ที่เหลือจับคอลัมน์จากชื่อ (label) ให้ตรงประเภท; คอลัมน์อื่นเว้นว่างให้กรอกเอง.
  const computedKindOf = (t: Pick<PayoutType, 'code' | 'label'>): 'holiday' | 'diligence' | 'late' | 'forget_in' | 'forget_out' | null => {
    if (t.code === 'forget_in' || t.code === 'forget_out') return t.code;
    if (t.label.includes('นักขัต')) return 'holiday';
    if (t.label.includes('ขยัน')) return 'diligence';
    if (t.label.includes('สาย')) return 'late';
    return null;
  };

  // เทียบยอดที่ระบบคิดกับยอดในรอบรายคน ให้ HR เห็นก่อนจ่าย (แก้เองได้ ระบบแค่เตือน)
  const computedCheck = usePayoutRoundComputed(id, isDraft && round?.round_kind === 'commission' && rows.length > 0);
  const computedMismatches = useMemo(() => {
    const computedRows = computedCheck.data?.rows;
    if (!computedRows || rows.length === 0) return [];
    const rowByEmp = new Map(rows.map((r) => [r.emp_code, r]));
    const empByCode = new Map((employees ?? []).map((e) => [e.emp_code, e]));
    const out: Array<{ emp_code: string; name: string; label: string; system: number; entered: number; reason: string }> = [];
    for (const c of computedRows) {
      const row = rowByEmp.get(c.emp_code);
      if (!row && removed.has(c.emp_code)) continue;
      const emp = empByCode.get(c.emp_code);
      const name = row?.name ?? (emp ? `${emp.first_name} ${emp.last_name}`.trim() : c.emp_code);
      for (const t of activeTypes) {
        const kind = computedKindOf(t);
        if (!kind) continue;
        const system = c[kind];
        // worker เก่ายังไม่ส่งฟิลด์นี้ หรือยังคิดลืมสแกนไม่ได้ (มีแถบเตือนแยกแล้ว) = ไม่มีตัวเลขให้เทียบ
        if (system === undefined) continue;
        if ((kind === 'forget_in' || kind === 'forget_out') && computedCheck.data?.forget_rule === 'unavailable') continue;
        const entered = Number(row?.values[t.code]) || 0;
        if (Math.abs(entered - system) >= 1) {
          const forgetCount = kind === 'forget_in' ? c.forget_in_count : kind === 'forget_out' ? c.forget_out_count : undefined;
          const reason = kind === 'diligence'
            ? (c.diligence_reasons ?? []).map((code) => DILIGENCE_REASON_LABEL[code] ?? code).join(', ')
            : forgetCount ? `ลืม ${forgetCount} ครั้ง` : '';
          out.push({ emp_code: c.emp_code, name, label: t.label, system, entered, reason });
        }
      }
    }
    return out;
  }, [computedCheck.data, rows, removed, employees, activeTypes]);
  const mismatchPeople = new Set(computedMismatches.map((m) => m.emp_code)).size;

  async function downloadTemplate() {
    // ดึงตัวเลขที่ระบบคิดให้ต่อคน — ล้มเหลวก็ยังโหลดเทมเพลตเปล่าได้
    // ⭐ รอบเงินเดือนไม่เติม: นักขัต/เบี้ยขยัน/สาย(คนมีค่าคอม)/ลืมสแกน จ่าย-หักในรอบค่าคอม
    //   ถ้าเติมในชีทเงินเดือนด้วย นำเข้าแล้วจ่าย/หักซ้ำ 2 รอบ
    let computed = new Map<string, PayoutComputedRow>();
    if (!/เงินเดือน/.test(round?.title ?? '')) {
      try {
        const res = await apiFetch<PayoutComputed>(`/hr/payouts/rounds/${id}/computed`);
        computed = new Map((res.rows ?? []).map((r) => [r.emp_code, r]));
        if (res.warning) toast.error(res.warning);
        else if (res.window_start && res.window_end) {
          // ให้ HR เห็นว่าคิด นักขัต/สาย/ขาด/เบี้ยขยัน/ลืมสแกน จากช่วงรอบตัดวีคไหน (ไม่ใช่เดือนปฏิทิน)
          toast.success(`ระบบคิด นักขัต/สาย/เบี้ยขยัน/ลืมสแกน จากรอบตัด ${res.window_start} ถึง ${res.window_end}`);
        }
      } catch {
        toast.error('ดึงตัวเลขที่ระบบคิดให้ไม่สำเร็จ — เทมเพลตจะออกมาแบบเว้นว่าง กรอกเองได้');
      }
    }
    const header = ['รหัสพนักงาน', 'ชื่อ-สกุล', 'ตำแหน่ง', ...activeTypes.map((t) => t.label)];
    const lines = [header, ...eligibleEmployees.map((e) => {
      const c = computed.get(e.emp_code);
      return [
        e.emp_code,
        `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '') + (leaverOf(e).left ? ` [ลาออก ${leaverOf(e).endDate ?? '-'}]` : ''),
        e.position ?? '',
        ...activeTypes.map((t) => {
          const kind = computedKindOf(t);
          if (!kind || !c) return '';
          const v = c[kind];
          return v !== undefined && v > 0 ? String(v) : '';
        }),
      ];
    })];
    const csv = '﻿' + lines.map((row) => row.map((c) => (/[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c)).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'payout-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (detail.isLoading || !round) return <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} className="btn-secondary btn-slim inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> รอบจ่ายทั้งหมด
        </button>
        <span className={STATUS_BADGE[round.status].cls}>{STATUS_BADGE[round.status].label}</span>
      </div>

      <div className="card p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold text-gray-900 flex items-center gap-2">
              {round.status === 'paid' && <Lock className="w-4 h-4 text-gray-400" />}
              {round.title}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {round.period
                ? <><b className="text-gray-700">งวด {periodLabel(round.period)}</b> · </>
                : <><b className="text-amber-700">ยังไม่ได้ตั้งงวด</b> · </>}
              จ่ายวันที่ {thaiDate(round.pay_date)} · หัก ณ ที่จ่ายตั้งต้น {Number(round.wht_default_percent)}%
              {round.note && <> · {round.note}</>}
            </div>
          </div>
          {isDraft && (
            <button onClick={() => setEditingMeta(true)} className="p-1.5 rounded hover:bg-gray-100 shrink-0">
              <Pencil className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
        <p className="flex items-start gap-1.5 text-[11px] text-gray-500 mt-2 pt-2 border-t border-gray-100">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px text-gray-400" />
          <span>
            &ldquo;หัก ณ ที่จ่าย&rdquo; ของรอบนี้ = ภาษีของค่าคอม/OT เท่านั้น
            คนละตัวกับภาษีเงินเดือนในทะเบียนพนักงาน · คิด {Number(round.wht_default_percent)}% จาก
            ยอดหลังหักรายการอื่น (รวมเพิ่ม − รวมหัก) แก้รายคนได้ในตาราง
          </span>
        </p>
      </div>

      {/* ⚠️ เตือนหักภาษีซ้ำ 2 ชั้น — มีประเภทรายการหักที่ทับกับช่อง "หัก ณ ที่จ่าย" */}
      {/* แสดงเฉพาะเมื่อซ้ำจริง (มียอดคอลัมน์ภาษี + ยังโดน % ของรอบ) — ประเภทภาษีของชีทเงินเดือนมีประจำแล้ว */}
      {doubleTaxRows.length > 0 && (
        <div className="card p-3 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900 flex-1">
              <div className="font-medium">
                ระวังหักภาษีซ้ำ — {doubleTaxRows.length} คนมียอดในคอลัมน์ {whtLikeTypes.map((t) => `“${t.label}”`).join(' / ')} และยังโดน &ldquo;หัก ณ ที่จ่าย&rdquo; อีกชั้น
              </div>
              <p className="text-xs text-amber-800 mt-1">
                ถ้าคอลัมน์นั้นคือภาษีที่คิดมาแล้ว ให้ตั้ง &ldquo;หัก ณ ที่จ่าย&rdquo; ของคนกลุ่มนี้เป็น 0
              </p>
              <button
                onClick={() => zeroWhtFor(doubleTaxRows.map((r) => r.emp_code))}
                className="btn-secondary btn-slim mt-2"
              >
                ตั้งหัก ณ ที่จ่าย = 0 ให้ {doubleTaxRows.length} คนนี้
              </button>
            </div>
          </div>
        </div>
      )}

      {/* เทียบยอดกับที่ระบบคิด — เตือนอย่างเดียว HR ยังแก้เองและจ่ายได้ตามปกติ */}
      {computedCheck.data?.warning && (
        <div className="card p-3 border-amber-300 bg-amber-50 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <span>{computedCheck.data.warning}</span>
        </div>
      )}
      {computedMismatches.length > 0 && (
        <details className="card p-3 border-amber-300 bg-amber-50">
          <summary className="cursor-pointer text-sm font-medium text-amber-900">
            {mismatchPeople} คน มียอดไม่ตรงกับที่ระบบคิด
            {computedCheck.data?.window_start && <> (รอบ {computedCheck.data.window_start} ถึง {computedCheck.data.window_end})</>}
          </summary>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs min-w-[600px]">
              <thead>
                <tr className="text-left text-amber-800 border-b border-amber-200">
                  <th className="py-1 pr-2 font-medium">พนักงาน</th>
                  <th className="py-1 pr-2 font-medium">รายการ</th>
                  <th className="py-1 pr-2 font-medium text-right">ระบบคิด</th>
                  <th className="py-1 pr-2 font-medium text-right">ในรอบนี้</th>
                  <th className="py-1 font-medium">เหตุผล</th>
                </tr>
              </thead>
              <tbody>
                {computedMismatches.map((m) => (
                  <tr key={`${m.emp_code}-${m.label}`} className="border-b border-amber-100 last:border-0">
                    <td className="py-1 pr-2 text-amber-950">{m.emp_code} · {m.name}</td>
                    <td className="py-1 pr-2 text-amber-950">{m.label}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{fmt(m.system)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{fmt(m.entered)}</td>
                    <td className="py-1 text-amber-950">{m.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* แถบเครื่องมือ */}
      <div className="flex items-center gap-2 flex-wrap">
        {isDraft && (
          <>
            <button onClick={() => setAdding(true)} className="btn-secondary btn-slim inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> เพิ่มพนักงาน
            </button>
            <label className="btn-secondary btn-slim inline-flex items-center gap-1 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> นำเข้าไฟล์
              <input
                type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImport(f); e.target.value = ''; }}
              />
            </label>
            <button onClick={downloadTemplate} className="btn-secondary btn-slim inline-flex items-center gap-1">
              <FileSpreadsheet className="w-3.5 h-3.5" /> เทมเพลต
            </button>
          </>
        )}
        <span className="flex-1" />
        <button onClick={() => void apiDownload(`/hr/payouts/rounds/${id}/export`, `payout-${round.pay_date}.csv`)} className="btn-secondary btn-slim inline-flex items-center gap-1">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        {/* ⭐ M3: รอบยกเลิกแล้ว ห้ามออกไฟล์โอน (backend ปฏิเสธเช่นกัน — ซ่อนปุ่มกันงง) */}
        {!isCancelled && (
          <button
            onClick={() => void handleExportBankTransfer()}
            disabled={bankExporting}
            className="btn-secondary btn-slim inline-flex items-center gap-1 disabled:opacity-50"
            title={round.bank_transfer_exported_at ? `เคย export แล้วเมื่อ ${new Date(round.bank_transfer_exported_at).toLocaleString('th-TH')}` : undefined}
          >
            <Landmark className="w-3.5 h-3.5" /> {bankExporting ? 'กำลังออกไฟล์…' : 'ไฟล์โอนธนาคาร'}
            {(round.bank_transfer_export_count ?? 0) > 0 && (
              <span className="text-[10px] text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5">เคย export {round.bank_transfer_export_count} ครั้ง</span>
            )}
          </button>
        )}
      </div>

      {/* กติกาการอ่านคอลัมน์ WHT จากไฟล์ — เดิมมองไม่เห็นเลย ทั้งที่ตัดสินเงินจริง
          (ไฟล์มีคอลัมน์นี้ = ทับ % ของรอบทั้งใบ · เว้นว่าง = ไม่หัก) */}
      {isDraft && (
        <p className="text-[11px] text-gray-500 -mt-1">
          ไฟล์นำเข้า: ถ้า<strong className="text-gray-700">ไม่มี</strong>คอลัมน์ &ldquo;หัก ณ ที่จ่าย&rdquo; ทุกคนใช้ {Number(round.wht_default_percent)}% ของรอบ ·
          ถ้า<strong className="text-gray-700">มี</strong> ระบบใช้<strong className="text-gray-700">ยอดบาท</strong>ในไฟล์แทน (เว้นว่าง = ไม่หัก) ·
          ถ้ากรอกคอลัมน์ &ldquo;ภาษี 3%&rdquo; / &ldquo;ภ.ง.ด.1&rdquo; มาเอง ระบบไม่หัก % ของรอบซ้ำ ·
          หัวคอลัมน์ต้องตรงชื่อประเภทรายการ (ไม่สนช่องว่าง/จุด/ตัวพิมพ์)
        </p>
      )}

      {/* ตาราง */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
              <th className="px-3 py-2 font-medium">พนักงาน</th>
              {activeTypes.map((t) => (
                <th key={t.code} className={`px-2 py-2 font-medium text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-500' : ''}`}>
                  {t.label}
                </th>
              ))}
              <th className="px-2 py-2 font-medium text-right whitespace-nowrap">{hasDeduct ? 'รวมเพิ่ม' : 'รวม'}</th>
              {hasDeduct && <th className="px-2 py-2 font-medium text-right text-red-500 whitespace-nowrap">รวมหัก</th>}
              {/* หัวเดิมเขียน "หัก ณ ที่จ่าย %" แต่แถวที่มายอดบาทจากไฟล์ไม่ใช่ % =
                  หัวตารางโกหกครึ่งคอลัมน์ → บอกหน่วยที่ระดับเซลล์แทน */}
              <th className="px-2 py-2 font-medium text-right whitespace-nowrap">
                หัก ณ ที่จ่าย
                <span className="block text-[10px] font-normal text-gray-400">ตั้งต้น {Number(round.wht_default_percent)}%</span>
              </th>
              <th className="px-2 py-2 font-medium text-right">สุทธิ</th>
              {(isDraft || isPaid) && <th className="px-2 py-2 w-8" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={activeTypes.length + (hasDeduct ? 5 : 4)} className="px-3 py-8 text-center text-gray-400">
                ยังไม่มีรายการ — กด "เพิ่มพนักงาน" หรือ "นำเข้าไฟล์"
              </td></tr>
            )}
            {rows.map((r) => {
              const c = calc(r);
              return (
                <tr key={r.emp_code} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5">
                    <div className="text-gray-900 whitespace-nowrap flex items-center gap-1.5">
                      {r.name}
                      {(() => {
                        const e = empByCode.get(r.emp_code);
                        const lv = e ? leaverOf(e) : { left: false, endDate: null };
                        return lv.left ? (
                          <span className="inline-block text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5 whitespace-nowrap font-normal">
                            {lv.endDate ? `ลาออก ${lv.endDate}` : 'ลาออก'}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-[11px] text-gray-400">{r.dept || '—'} · <span className="font-mono">{r.emp_code}</span></div>
                  </td>
                  {activeTypes.map((t) => (
                    <td key={t.code} className="px-2 py-1.5 text-right">
                      {isDraft ? (
                        <input
                          type="number" step="0.01" inputMode="decimal"
                          value={r.values[t.code] ?? ''}
                          onChange={(e) => setValue(r.emp_code, t.code, e.target.value)}
                          className={`w-24 rounded-lg border px-2 py-1 text-xs text-right focus:outline-none ${t.kind === 'deduction' ? 'border-red-200 text-red-600 focus:border-red-400' : 'border-gray-200 focus:border-brand-400'}`}
                        />
                      ) : (
                        <span className={t.kind === 'deduction' ? 'text-red-600' : 'text-gray-700'}>
                          {r.values[t.code] ? (t.kind === 'deduction' ? `-${fmt(Number(r.values[t.code]))}` : fmt(Number(r.values[t.code]))) : ''}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-medium text-gray-900 whitespace-nowrap">{fmt(c.earn)}</td>
                  {hasDeduct && (
                    <td className="px-2 py-1.5 text-right font-medium text-red-600 whitespace-nowrap">{c.deduct > 0 ? `-${fmt(c.deduct)}` : ''}</td>
                  )}
                  {/* ⭐ คอลัมน์นี้มี 2 โหมดปนกัน: กรอก % (ค่าตั้งต้นของรอบ) กับ
                      ยอดบาทที่ล็อกมาจากไฟล์ลูกค้า. เดิมไม่บอกว่าแถวไหนเป็นโหมดไหน
                      และโหมด % ไม่โชว์เลยว่าหักจริงกี่บาท → โชว์ยอดบาทที่หักจริง
                      (c.whtAmt) ทุกโหมด แล้วกำกับที่มาไว้ใต้ */}
                  <td className="px-2 py-1.5 text-right">
                    {isDraft ? (
                      r.whtAmt !== undefined ? (
                        <>
                          <span className="inline-flex items-center gap-1 justify-end">
                            <span className="text-xs text-gray-700 whitespace-nowrap">{fmt(Number(r.whtAmt))} ฿</span>
                            <button
                              type="button"
                              onClick={() => clearWhtOverride(r.emp_code)}
                              className="p-0.5 text-gray-300 hover:text-red-500"
                              title="ล้างยอดจากไฟล์ — กลับไปคิดเป็น % ของรอบ"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                          <span className="block text-[10px] text-gray-400 leading-tight">ยอดจากไฟล์</span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 justify-end">
                            <input
                              type="number" step="0.01" min="0" max="100" inputMode="decimal"
                              value={r.wht}
                              onChange={(e) => setWhtRow(r.emp_code, e.target.value)}
                              className="w-14 rounded-lg border border-gray-200 px-1.5 py-1 text-xs text-right focus:border-brand-400 focus:outline-none"
                            />
                            <span className="text-[11px] text-gray-400">%</span>
                          </span>
                          <span className="block text-[10px] text-gray-400 leading-tight whitespace-nowrap">
                            = {fmt(c.whtAmt)} ฿
                          </span>
                        </>
                      )
                    ) : (
                      <>
                        <span className="text-gray-700 whitespace-nowrap">{fmt(c.whtAmt)} ฿</span>
                        <span className="block text-[10px] text-gray-400 leading-tight">
                          {r.whtAmt !== undefined ? 'ยอดจากไฟล์' : `${Number(r.wht)}% ของยอดหลังหัก`}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmt(c.net)}</td>
                  {isDraft && (
                    <td className="px-2 py-1.5">
                      <button onClick={() => removeRow(r.emp_code)} className="p-1 text-gray-300 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                  {isPaid && (
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => void downloadSlip(r.emp_code)}
                        disabled={slipLoading === r.emp_code}
                        className="p-1 text-gray-400 hover:text-brand-600 disabled:opacity-40"
                        title="ดาวน์โหลดสลิป PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200 text-sm font-semibold text-gray-900">
                <td className="px-3 py-2" colSpan={1 + activeTypes.length}>{rows.length} คน</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">{fmt(totals.earn)}</td>
                {hasDeduct && <td className="px-2 py-2 text-right text-red-600 whitespace-nowrap">{totals.deduct > 0 ? `-${fmt(totals.deduct)}` : ''}</td>}
                <td className="px-2 py-2 text-right text-gray-500 font-normal whitespace-nowrap">-{fmt(totals.wht)} ฿</td>
                <td className="px-2 py-2 text-right whitespace-nowrap">฿{fmt(totals.net)}</td>
                {(isDraft || isPaid) && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ปุ่มหลัก */}
      {isDraft ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => void handleSave()} disabled={saveItems.isPending || !dirty} className="btn-secondary inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> {saveItems.isPending ? 'กำลังบันทึก…' : 'บันทึกร่าง'}
          </button>
          <button onClick={() => void handleMarkPaid()} disabled={markPaid.isPending || rows.length === 0} className="btn-cta inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> ยืนยันจ่ายแล้ว (ล็อกรอบ)
          </button>
          <span className="flex-1" />
          <button onClick={() => void handleCancel()} className="btn-secondary text-red-700 border-red-200 inline-flex items-center gap-1.5">
            <Ban className="w-4 h-4" /> ยกเลิกรอบ
          </button>
        </div>
      ) : round.status === 'paid' ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" /> จ่ายแล้ว {round.paid_at ? `เมื่อ ${new Date(round.paid_at).toLocaleString('th-TH')}` : ''} — แก้ไขไม่ได้ · พนักงานเห็นสลิปใน LINE แล้ว
          </p>
          <div className="flex items-center gap-2">
            {round.round_kind === 'salary' && !round.slip_released && (
              <button onClick={() => void handleReleaseSlip()} className="btn-secondary btn-slim inline-flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> อนุมัติออกสลิปงวดนี้
              </button>
            )}
            <button onClick={() => void handleCancel()} className="btn-secondary btn-slim text-red-700 border-red-200 inline-flex items-center gap-1">
              <Ban className="w-3.5 h-3.5" /> ยกเลิกรอบ (เจ้าของ)
            </button>
          </div>
        </div>
      ) : null}

      {adding && (
        <Modal open onClose={() => setAdding(false)} title="เพิ่มพนักงานเข้ารอบจ่าย" size="max-w-lg">
          <AddEmployeesPanel
            employees={eligibleEmployees}
            existing={new Set(rows.map((r) => r.emp_code))}
            onAdd={(codes) => { addEmployees(codes); setAdding(false); }}
          />
        </Modal>
      )}
      {pendingLeavers.length > 0 && (
        <LeaverConfirmModal
          people={pendingLeavers}
          periodText={roundPeriod ? periodLabel(roundPeriod) : 'งวดนี้'}
          onClose={() => setPendingLeavers([])}
          onConfirm={(picked) => {
            if (picked.length > 0) {
              setRows((rs) => [...rs.filter((r) => !picked.some((p) => p.emp_code === r.emp_code)), ...picked.map((p) => p.row)]
                .sort((a, b) => a.emp_code.localeCompare(b.emp_code)));
              setRemoved((s) => { const next = new Set(s); picked.forEach((p) => next.delete(p.emp_code)); return next; });
              setDirty(true);
              toast.success(`เพิ่มคนที่ลาออกแล้ว ${picked.length} คนเข้ารอบนี้`);
            }
            setPendingLeavers([]);
          }}
        />
      )}
      {editingMeta && round && (
        <RoundMetaModal
          initial={{ id: round.id, title: round.title, pay_date: round.pay_date, wht_default_percent: Number(round.wht_default_percent), note: round.note, period: round.period ?? null }}
          onClose={() => setEditingMeta(false)}
        />
      )}
    </div>
  );
}

function AddEmployeesPanel({ employees, existing, onAdd }: {
  employees: PickerEmployee[];
  existing: Set<string>;
  onAdd: (codes: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const available = employees.filter((e) => !existing.has(e.emp_code));
  return (
    <div className="space-y-3">
      <EmployeeMultiPicker
        employees={available}
        isChecked={(e) => picked.has(e.emp_code)}
        onToggle={(e, on) => setPicked((s) => { const n = new Set(s); if (on) n.add(e.emp_code); else n.delete(e.emp_code); return n; })}
        onToggleAll={(codes, all) => setPicked(all ? new Set() : new Set(codes))}
        selectedCount={picked.size}
        emptyText="พนักงานทุกคนอยู่ในรอบนี้แล้ว"
      />
      <button onClick={() => onAdd([...picked])} disabled={picked.size === 0} className="btn-cta w-full justify-center flex items-center gap-2">
        <Plus className="w-4 h-4" /> เพิ่ม {picked.size || ''} คน
      </button>
    </div>
  );
}

/**
 * คนในไฟล์ที่ลาออกไปก่อนงวดนี้ — ให้ HR ยืนยันทีละคนว่าจะจ่ายไหม (เผื่อเคสจ่ายตกหล่น
 * ย้อนหลังจริง) แทนที่จะข้ามเงียบแบบเดิม หรือจ่ายให้อัตโนมัติแบบที่กลัวกัน
 */
function LeaverConfirmModal({ people, periodText, onClose, onConfirm }: {
  people: Array<{ emp_code: string; name: string; endDate: string | null; row: EditRow }>;
  periodText: string;
  onClose: () => void;
  onConfirm: (picked: Array<{ emp_code: string; name: string; endDate: string | null; row: EditRow }>) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  return (
    <Modal open onClose={onClose} title="ในไฟล์มีคนที่ลาออกไปแล้ว" size="max-w-lg">
      <div className="space-y-3">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          {people.length} คนนี้อยู่ในไฟล์ แต่ลาออกไปก่อนงวด <span className="font-semibold">{periodText}</span> แล้ว
          — ระบบไม่ใส่ให้อัตโนมัติ ติ๊กเฉพาะคนที่ยืนยันว่าต้องจ่ายจริง (ไม่ติ๊ก = ไม่จ่าย)
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {people.map((p) => (
            <label key={p.emp_code} className="flex items-center gap-2 border border-gray-100 rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={picked.has(p.emp_code)}
                onChange={(e) => setPicked((s) => { const n = new Set(s); if (e.target.checked) n.add(p.emp_code); else n.delete(p.emp_code); return n; })}
                className="w-4 h-4 flex-shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{p.name}</span>
                <span className="text-xs text-gray-400">{p.emp_code}</span>
              </span>
              <span className="inline-block text-[11px] bg-red-100 text-red-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                {p.endDate ? `ลาออก ${p.endDate}` : 'ยังไม่กรอกวันสิ้นสุด'}
              </span>
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">ไม่จ่ายใครเลย</button>
          <button onClick={() => onConfirm(people.filter((p) => picked.has(p.emp_code)))} disabled={picked.size === 0} className="btn-cta flex-1">
            จ่ายให้ {picked.size || ''} คนที่ติ๊ก
          </button>
        </div>
      </div>
    </Modal>
  );
}

// === สรุปทั้งปี ===
function YearReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const report = usePayoutReport(year);
  const types = usePayoutTypes();

  const usedCodes = useMemo(() => {
    const s = new Set<string>();
    for (const r of report.data?.employees ?? []) for (const k of Object.keys(r.by_type)) s.add(k);
    return s;
  }, [report.data]);
  const cols = (types.data ?? []).filter((t) => usedCodes.has(t.code));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-auto">
          {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>ปี {y + 543}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400">นับเฉพาะรอบที่ยืนยันจ่ายแล้ว — ใช้ยอด "หัก ณ ที่จ่าย" ประกอบการยื่นภาษีกับสำนักงานบัญชี</p>
      </div>
      {report.isLoading ? (
        <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>
      ) : (report.data?.employees ?? []).length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีรอบที่จ่ายแล้วในปีนี้</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium">พนักงาน</th>
                <th className="px-2 py-2 font-medium text-right">กี่รอบ</th>
                {cols.map((t) => (
                  <th key={t.code} className={`px-2 py-2 font-medium text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-500' : ''}`}>
                    {t.label}
                  </th>
                ))}
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">รวมเพิ่ม</th>
                <th className="px-2 py-2 font-medium text-right text-red-500 whitespace-nowrap">รวมหัก</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">หัก ณ ที่จ่าย</th>
                <th className="px-2 py-2 font-medium text-right">สุทธิ</th>
              </tr>
            </thead>
            <tbody>
              {(report.data?.employees ?? []).map((r) => (
                <tr key={r.emp_code} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5">
                    <div className="text-gray-900 whitespace-nowrap">
                      {r.employee ? `${r.employee.first_name} ${r.employee.last_name}`.trim() + (r.employee.nickname ? ` (${r.employee.nickname})` : '') : r.emp_code}
                    </div>
                    <div className="text-[11px] text-gray-400">{r.employee?.department ?? '—'} · <span className="font-mono">{r.emp_code}</span></div>
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-500">{r.rounds}</td>
                  {cols.map((t) => (
                    <td key={t.code} className={`px-2 py-1.5 text-right whitespace-nowrap ${t.kind === 'deduction' ? 'text-red-600' : 'text-gray-700'}`}>
                      {r.by_type[t.code] ? (t.kind === 'deduction' ? `-${fmt(r.by_type[t.code]!)}` : fmt(r.by_type[t.code]!)) : ''}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-medium whitespace-nowrap">{fmt(r.gross)}</td>
                  <td className="px-2 py-1.5 text-right text-red-600 whitespace-nowrap">{(r.deduct ?? 0) > 0 ? `-${fmt(r.deduct)}` : ''}</td>
                  <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">{fmt(r.wht)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">฿{fmt(r.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// === จัดการประเภทรายการ ===
function TypesTab() {
  const types = usePayoutTypes();
  const upsert = useUpsertPayoutType();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<PayoutType> | null>(null);

  function save() {
    if (!editing?.label?.trim()) { toast.error('ตั้งชื่อประเภทก่อน'); return; }
    const code = editing.code?.trim() || newTypeCode(editing.label.trim(), new Set((types.data ?? []).map((t) => t.code)));
    upsert.mutate(
      { id: editing.id, code, label: editing.label.trim(), kind: editing.kind ?? 'earning', sort_order: editing.sort_order ?? 100, active: editing.active ?? true },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setEditing(null); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">ประเภทรายการ = คอลัมน์ในตารางรอบจ่าย (ตรงกับหัวคอลัมน์ไฟล์ Excel เดิมของบริษัท)</p>
        <button onClick={() => setEditing({})} className="btn-secondary btn-slim inline-flex items-center gap-1 shrink-0">
          <Plus className="w-3.5 h-3.5" /> เพิ่มประเภท
        </button>
      </div>
      <div className="card divide-y divide-gray-50">
        {(types.data ?? []).map((t) => (
          <div key={t.id} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <span className={`text-sm ${t.active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{t.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ml-2 ${t.kind === 'deduction' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                {t.kind === 'deduction' ? 'เงินหัก' : 'เงินเพิ่ม'}
              </span>
              <span className="text-[11px] text-gray-400 font-mono ml-2">{t.code}</span>
            </div>
            <button onClick={() => setEditing(t)} className="p-1.5 rounded hover:bg-gray-100">
              <Pencil className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal open onClose={() => setEditing(null)} title={editing.id ? 'แก้ประเภทรายการ' : 'เพิ่มประเภทรายการ'}>
          <div className="space-y-3">
            <div>
              <label className="label">ชื่อที่แสดง *</label>
              <input className="input" value={editing.label ?? ''} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="เช่น ค่าคอมไลฟ์สด" />
            </div>
            <div>
              <label className="label">ชนิดรายการ</label>
              <div className="flex gap-2">
                {([['earning', 'เงินเพิ่ม'], ['deduction', 'เงินหัก']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setEditing({ ...editing, kind: k })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm ${(editing.kind ?? 'earning') === k
                      ? (k === 'deduction' ? 'border-red-400 bg-red-50 text-red-700 font-medium' : 'border-emerald-400 bg-emerald-50 text-emerald-700 font-medium')
                      : 'border-gray-200 text-gray-500'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">เงินหัก = ระบบเอาไปลบจากยอดจ่าย (กรอกตัวเลขเป็นบวก เช่น หักแชท 200)</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ลำดับ</label>
                <input type="number" className="input" value={editing.sort_order ?? 100} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} />
              </div>
              <label className="flex items-end gap-2 pb-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.active ?? true} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                ใช้งานอยู่
              </label>
            </div>
            <button onClick={save} disabled={upsert.isPending} className="btn-cta w-full justify-center flex items-center gap-2">
              <Save className="w-4 h-4" /> บันทึก
            </button>
            <button onClick={() => setEditing(null)} className="btn-secondary w-full justify-center flex items-center gap-2">
              <X className="w-4 h-4" /> ปิด
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
